
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface ApproveWorksheetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (status: "APPROVED" | "REJECTED", note: string) => Promise<void>;
  loading: boolean;
  initialStatus?: "WAITING" | "APPROVED" | "REJECTED";
  initialNote?: string;
  isEditing?: boolean;
}

export const ApproveWorksheetDialog: React.FC<ApproveWorksheetDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  loading,
  initialStatus = "WAITING",
  initialNote = "",
  isEditing = false,
}) => {
  const [status, setStatus] = useState<"APPROVED" | "REJECTED">(
    initialStatus === "REJECTED" ? "REJECTED" : "APPROVED"
  );
  const [note, setNote] = useState(initialNote || "");

  useEffect(() => {
    if (open) {
      setStatus(initialStatus === "REJECTED" ? "REJECTED" : "APPROVED");
      setNote(initialNote || "");
    }
  }, [open, initialStatus, initialNote]);

  const handleSubmit = async () => {
    await onSubmit(status, note);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? "แก้ไขผลการตรวจ Worksheet" : "ตรวจสอบ Worksheet"}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="space-y-4">
            <div>
              <h4 className="mb-2 font-medium">ผลการตรวจ:</h4>
              <RadioGroup
                value={status}
                onValueChange={(value) =>
                  setStatus(value as "APPROVED" | "REJECTED")
                }
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="APPROVED" id="approved" />
                  <Label htmlFor="approved" className="text-green-600">
                    อนุมัติ
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="REJECTED" id="rejected" />
                  <Label htmlFor="rejected" className="text-red-600">
                    ไม่อนุมัติ
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label htmlFor="note" className="mb-2 block font-medium">
                หมายเหตุ:
              </Label>
              <Textarea
                id="note"
                placeholder="กรุณาระบุหมายเหตุ (ถ้ามี)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-24"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "กำลังบันทึก..." : isEditing ? "บันทึกการแก้ไข" : "บันทึก"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
